import { useEffect, useState } from 'react';
import { Alert, Button, Form, InputNumber, Modal, Select, Space, Table, Tag, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import {
    useLazyGetDepartmentClientsQuery,
    useAddDepartmentClientMutation,
    useDeleteDepartmentClientMutation,
} from '../../redux/api/departments';
import { incidentTypes, producerTypes, workflowTypes } from '../../util/constant';

interface DepartmentClientRow {
    id: number;
    clientId: number;
    departmentId: number;
    workflowType: string;
    producerType: string;
    incidentType: string;
}

const PAGE_SIZE = 25;

const extractErrorMessage = (e: any): string => {
    if (typeof e?.data === 'string' && e.data) return e.data;
    if (typeof e?.data?.message === 'string') return e.data.message;
    if (typeof e?.error === 'string') return e.error;
    return 'An unexpected error occurred.';
};

const DepartmentMappingPage = () => {

    const [mappings, setMappings] = useState<DepartmentClientRow[]>([]);
    const [page, setPage] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
    const [tableHeight, setTableHeight] = useState(0);
    const [isAddOpen, setAddOpen] = useState(false);
    const [addError, setAddError] = useState<string | null>(null);

    const [addForm] = Form.useForm();
    const [modal, contextHolder] = Modal.useModal();

    const [getDepartmentClients] = useLazyGetDepartmentClientsQuery();
    const [postDepartmentClient] = useAddDepartmentClientMutation();
    const [removeDepartmentClient] = useDeleteDepartmentClientMutation();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 72,
        },
        {
            title: 'Client ID',
            dataIndex: 'clientId',
            key: 'clientId',
            width: 104,
        },
        {
            title: 'Department ID',
            dataIndex: 'departmentId',
            key: 'departmentId',
            width: 136,
        },
        {
            title: 'Workflow Type',
            dataIndex: 'workflowType',
            key: 'workflowType',
            width: 160,
            render: (v: string) => v ? <Tag>{v.replace(/_/g, ' ')}</Tag> : '',
        },
        {
            title: 'Producer Type',
            dataIndex: 'producerType',
            key: 'producerType',
            width: 136,
            render: (v: string) => v ? <Tag>{v}</Tag> : '',
        },
        {
            title: 'Incident Type',
            dataIndex: 'incidentType',
            key: 'incidentType',
            width: 136,
            render: (v: string) => v ? <Tag>{v}</Tag> : '',
        },
        {
            title: 'Action',
            key: 'action',
            width: 96,
            render: (_: any, record: DepartmentClientRow) => (
                <Button danger size="small" onClick={() => onDelete(record)}>
                    Remove
                </Button>
            ),
        },
    ];

    useEffect(() => {
        const updateTableHeight = () => setTableHeight(window.innerHeight - 288);
        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, []);

    useEffect(() => {
        fetchMappings(page.current);
    }, [page.current]);

    const fetchMappings = async (index: number) => {
        try {
            const data: any = await getDepartmentClients({ index, viewableSize: PAGE_SIZE }).unwrap();
            setMappings(data.items.map((m: DepartmentClientRow) => ({ ...m, key: m.id })));
            setPage(prev => ({ ...prev, total: data.page.totalSize }));
        } catch (e) {
            notification.error({
                message: 'Failed to load mappings',
                description: 'An error occurred while fetching department mappings.',
            });
        }
    };

    const handleTableChange = (pagination: any) => {
        setPage(prev => ({ ...prev, current: pagination.current }));
    };

    const openAddModal = () => {
        setAddError(null);
        setAddOpen(true);
    };

    const closeAddModal = () => {
        setAddOpen(false);
        setAddError(null);
        addForm.resetFields();
    };

    const addMapping = async (values: any) => {
        setAddError(null);
        try {
            await postDepartmentClient(values).unwrap();
            closeAddModal();
            fetchMappings(page.current);
            notification.success({
                message: 'Mapping Added',
                description: 'The department-to-client mapping has been created.',
            });
        } catch (e: any) {
            console.log(e);
            setAddError(extractErrorMessage(e));
        }
    };

    const deleteMapping = async (record: DepartmentClientRow) => {
        try {
            await removeDepartmentClient(record.id).unwrap();
            fetchMappings(page.current);
            notification.success({
                message: 'Mapping Removed',
                description: `Mapping for department ${record.departmentId} has been removed.`,
            });
        } catch (e) {
            notification.error({
                message: 'Failed to Remove Mapping',
                description: 'An error occurred while removing the mapping.',
            });
        }
    };

    const onDelete = (record: DepartmentClientRow) => {
        modal.confirm({
            title: 'Remove Mapping',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to remove the mapping for department ${record.departmentId}?`,
            okText: 'Remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => deleteMapping(record),
        });
    };

    return (
        <>
            {contextHolder}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Department Mapping</h1>
                <Space>
                    <Button type="primary" onClick={openAddModal}>
                        Add Mapping
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={mappings}
                columns={columns}
                rowKey="id"
                scroll={{ y: tableHeight }}
                pagination={{
                    current: page.current,
                    pageSize: page.pageSize,
                    total: page.total,
                }}
                onChange={handleTableChange}
            />

            <Modal
                title="Add Department Mapping"
                closable={false}
                open={isAddOpen}
                onOk={() => addForm.submit()}
                onCancel={closeAddModal}
                width={400}
                destroyOnClose={true}
            >
                {addError && (
                    <Alert
                        message={addError}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Form form={addForm} onFinish={addMapping} layout="vertical">
                    <Form.Item
                        label="Client ID"
                        name="clientId"
                        rules={[{ required: true, message: 'Client ID is required.' }]}
                    >
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Department ID"
                        name="departmentId"
                        rules={[{ required: true, message: 'Department ID is required.' }]}
                    >
                        <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label="Workflow Type"
                        name="workflowType"
                        rules={[{ required: true, message: 'Workflow Type is required.' }]}
                    >
                        <Select options={workflowTypes} />
                    </Form.Item>
                    <Form.Item
                        label="Producer Type"
                        name="producerType"
                        rules={[{ required: true, message: 'Producer Type is required.' }]}
                    >
                        <Select options={producerTypes} />
                    </Form.Item>
                    <Form.Item
                        label="Incident Type"
                        name="incidentType"
                        rules={[{ required: true, message: 'Incident Type is required.' }]}
                    >
                        <Select options={incidentTypes} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default DepartmentMappingPage;
